import { api } from '../axios';
import {
  PaymentVoucherUI,
  PaymentVoucherResponse,
  CreatePaymentVoucherRequest,
  UpdatePaymentVoucherRequest,
  PaymentVoucherListQuery,
  PaginatedPaymentVoucherResponse,
  PaymentVoucherStatusCounts,
  formatPaymentAmount
} from '@/types/paymentVoucher';

export interface ApiResponse<T> {
  data: T;
  message: string;
  timestamp: string;
}

export const paymentVoucherApi = {
  // Create a new payment voucher
  createPaymentVoucher: async (request: CreatePaymentVoucherRequest): Promise<PaymentVoucherResponse> => {
    const response = await api.post<ApiResponse<PaymentVoucherResponse>>('/payment-vouchers', request);
    return response.data.data;
  },

  // Get all payment vouchers with pagination and filtering
  getAllPaymentVouchers: async (query: PaymentVoucherListQuery): Promise<PaginatedPaymentVoucherResponse> => {
    const response = await api.get<PaginatedPaymentVoucherResponse>('/payment-vouchers', {
      params: query
    });
    return response.data;
  },

  // Get a specific payment voucher by code
  getPaymentVoucher: async (code: string): Promise<PaymentVoucherResponse> => {
    const response = await api.get<ApiResponse<PaymentVoucherResponse>>(`/payment-vouchers/${code}`);
    return response.data.data;
  },

  // Update an existing payment voucher
  updatePaymentVoucher: async (code: string, request: UpdatePaymentVoucherRequest): Promise<PaymentVoucherResponse> => {
    const response = await api.put<ApiResponse<PaymentVoucherResponse>>(`/payment-vouchers/${code}`, request);
    return response.data.data;
  },

  // Delete a payment voucher
  deletePaymentVoucher: async (code: string): Promise<void> => {
    await api.delete<ApiResponse<void>>(`/payment-vouchers/${code}`);
  },

  // Get payment voucher status counts
  getStatusCounts: async (query?: Partial<PaymentVoucherListQuery>): Promise<PaymentVoucherStatusCounts> => {
    const response = await api.get<ApiResponse<PaymentVoucherStatusCounts>>('/payment-vouchers/status-counts', {
      params: query
    });
    return response.data.data;
  },

  // Confirm a payment voucher
  confirmPaymentVoucher: async (code: string): Promise<PaymentVoucherResponse> => {
    const response = await api.post<ApiResponse<PaymentVoucherResponse>>(`/payment-vouchers/${code}/confirm`);
    return response.data.data;
  },

  // Cancel a payment voucher
  cancelPaymentVoucher: async (code: string): Promise<PaymentVoucherResponse> => {
    const response = await api.post<ApiResponse<PaymentVoucherResponse>>(`/payment-vouchers/${code}/cancel`);
    return response.data.data;
  }
};

// Transform payment voucher response to UI format
export const transformPaymentVoucherToUI = (paymentVoucher: PaymentVoucherResponse): PaymentVoucherUI => {
  return {
    ...paymentVoucher,
    status_display: paymentVoucher.current_status.status,
    receiver_name: getReceiverName(paymentVoucher),
    formatted_amount: formatPaymentAmount(paymentVoucher.amount)
  };
};

// Helper function to get receiver name
const getReceiverName = (paymentVoucher: PaymentVoucherResponse): string => {
  // Priority: receiver_name > supplier.name > user.name > receiver_id
  if (paymentVoucher.receiver_name) {
    return paymentVoucher.receiver_name;
  }
  if (paymentVoucher.receiver) {
    return (paymentVoucher.receiver as any).name || paymentVoucher.receiver_id;
  }
  if (paymentVoucher.supplier?.name) {
    return paymentVoucher.supplier.name;
  }
  if (paymentVoucher.user?.name) {
    return paymentVoucher.user.name;
  }
  return paymentVoucher.receiver_id;
};

// Format currency for display
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN').format(amount);
};

// Parse currency input
export const parseCurrency = (value: string): number => {
  // Remove all non-digit characters except decimal point
  const cleanValue = value.replace(/[^\d.,]/g, '');
  // Replace comma with dot for decimal separator
  const normalizedValue = cleanValue.replace(',', '.');
  return parseFloat(normalizedValue) || 0;
};

// Validate payment voucher form data
export const validatePaymentVoucherForm = (data: CreatePaymentVoucherRequest | UpdatePaymentVoucherRequest): string[] => {
  const errors: string[] = [];

  if ('payment_date' in data && data.payment_date && !isValidDate(data.payment_date)) {
    errors.push('Invalid payment date format');
  }

  if ('type' in data && data.type && !['supplier', 'employee', 'other'].includes(data.type)) {
    errors.push('Invalid receiver type');
  }

  // Validate receiver based on type
  if ('type' in data && data.type) {
    if (data.type === 'other') {
      // For 'other' type, receiver_name is required
      if (!('receiver_name' in data) || !data.receiver_name || data.receiver_name.trim().length === 0) {
        errors.push('Receiver name is required when receiver type is "Other"');
      }
      if (data.receiver_name && data.receiver_name.length > 200) {
        errors.push('Receiver name must be 200 characters or less');
      }
    } else {
      // For 'supplier' or 'employee' type, receiver_id is required
      if (!('receiver_id' in data) || !data.receiver_id || data.receiver_id.trim().length === 0) {
        errors.push('Receiver ID is required');
      }
    }
  } else {
    // If type is not provided but receiver_id is, validate it
    if ('receiver_id' in data && data.receiver_id && data.receiver_id.trim().length === 0) {
      errors.push('Receiver ID is required');
    }
  }

  if ('payment_type' in data && data.payment_type && !['supplier_payment', 'employee_payment', 'cash', 'transfer'].includes(data.payment_type)) {
    errors.push('Invalid payment type');
  }

  if ('payment_method' in data && data.payment_method && !['cash', 'transfer'].includes(data.payment_method)) {
    errors.push('Invalid payment method');
  }

  if ('amount' in data && data.amount !== undefined && (data.amount <= 0 || isNaN(data.amount))) {
    errors.push('Amount must be greater than 0');
  }

  if ('account_number' in data && data.account_number && data.account_number.length > 50) {
    errors.push('Account number must be 50 characters or less');
  }

  if ('transfer_note' in data && data.transfer_note && data.transfer_note.length > 200) {
    errors.push('Transfer note must be 200 characters or less');
  }

  if ('note' in data && data.note && data.note.length > 1000) {
    errors.push('Note must be 1000 characters or less');
  }

  return errors;
};

// Helper function to validate date format
const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

// Get payment voucher status badge color
export const getPaymentVoucherStatusColor = (status: string): string => {
  switch (status) {
    case 'created':
      return 'bg-yellow-100 text-yellow-800';
    case 'confirmed':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Get payment method display text
export const getPaymentMethodDisplay = (method: string): string => {
  switch (method) {
    case 'cash':
      return 'Tiền mặt';
    case 'transfer':
      return 'Chuyển khoản';
    default:
      return method;
  }
};

// Get payment type display text
export const getPaymentTypeDisplay = (type: string): string => {
  switch (type) {
    case 'supplier_payment':
      return 'Thanh toán nhà cung cấp';
    case 'employee_payment':
      return 'Thanh toán nhân viên';
    case 'cash':
      return 'Tiền mặt';
    case 'transfer':
      return 'Chuyển khoản';
    default:
      return type;
  }
};

// Get receiver type display text
export const getReceiverTypeDisplay = (type: string): string => {
  switch (type) {
    case 'supplier':
      return 'Nhà cung cấp';
    case 'employee':
      return 'Nhân viên';
    case 'other':
      return 'Khác';
    default:
      return type;
  }
};
